import React, { useState, useEffect, useRef } from 'react';
import {
  RiPlayCircleLine,
  RiPauseLine,
  RiSkipBackLine,
  RiSkipForwardLine,
  RiRefreshLine,
} from 'react-icons/ri';

const STEP_MS = 2200;

const STEPS = [
  { id: 1, label: "Task received",       description: "A new task arrives. The agent perceives its current environment — inputs, context, and available tools." },
  { id: 2, label: "Reasoning & planning", description: "The LLM brain analyzes the perceived state and plans the next best action to take toward the goal." },
  { id: 3, label: "Action taken",         description: "The agent executes its chosen action — calling a tool, querying an API, searching the web, or generating output." },
  { id: 4, label: "Observing result",     description: "The agent reads the outcome of its action and updates its understanding of the current state." },
  { id: 5, label: "Loop: another cycle",  description: "The updated state feeds back into perception. The agent cycles again — Perceive → Reason → Act → Observe." },
  { id: 6, label: "Goal met — done!",     description: "The reasoning step concludes the task is complete. The agent exits the loop and delivers the final answer." },
];

const ACC = {
  blue:   { stroke: '#0288d1', fill: 'rgba(2,136,209,0.13)'   },
  green:  { stroke: '#388e3c', fill: 'rgba(56,142,60,0.13)'   },
  purple: { stroke: '#7b1fa2', fill: 'rgba(123,31,162,0.13)'  },
  orange: { stroke: '#f59e0b', fill: 'rgba(245,158,11,0.13)'  },
};

// Which steps each element is highlighted in
const ACTIVE = {
  'n-start':    [1],
  'n-perceive': [1, 2, 5],
  'n-reason':   [2, 3, 6],
  'n-act':      [3, 4],
  'n-observe':  [4, 5],
  'n-finish':   [6],
  'a-sp': [1], // start→perceive
  'a-pr': [2], // perceive→reason
  'a-ra': [3], // reason→act
  'a-ao': [4], // act→observe
  'a-op': [5], // observe→perceive loop
  'a-rf': [6], // reason→finish
};

const N_ACC = {
  'n-start': 'blue', 'n-perceive': 'blue', 'n-reason': 'blue',
  'n-act': 'orange', 'n-observe': 'purple', 'n-finish': 'green',
};
const A_ACC = {
  'a-sp': 'blue', 'a-pr': 'blue', 'a-ra': 'orange',
  'a-ao': 'purple', 'a-op': 'purple', 'a-rf': 'green',
};

export default function AgentLoopDiagram() {
  const [step, setStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const timerRef = useRef(null);
  const maxStep = STEPS.length;

  useEffect(() => {
    if (isPlaying) {
      timerRef.current = setInterval(() => {
        setStep(prev => {
          if (prev >= maxStep) { setIsPlaying(false); clearInterval(timerRef.current); return prev; }
          return prev + 1;
        });
      }, STEP_MS);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [isPlaying, maxStep]);

  const on  = (id) => ACTIVE[id]?.includes(step) ?? false;
  const ns  = (id) => on(id) ? ACC[N_ACC[id]].stroke : 'var(--border-color, #ccc)';
  const nfill = (id) => on(id) ? ACC[N_ACC[id]].fill : 'var(--panel-background, #fff)';
  const nt  = (id) => on(id) ? ACC[N_ACC[id]].stroke : 'var(--text-secondary, #888)';
  const as_ = (id) => on(id) ? ACC[A_ACC[id]].stroke : 'var(--border-color, #ccc)';
  const am  = (id) => `url(#m-${on(id) ? A_ACC[id] : 'off'})`;

  const handlePlayPause = () => {
    if (step >= maxStep) { setStep(0); setIsPlaying(true); }
    else setIsPlaying(p => !p);
  };

  const currentStepData = step > 0 ? STEPS[step - 1] : null;

  // SVG layout (viewBox "0 0 370 415")
  // Main column centered at x=140
  // Nodes: Start(y=12,h=30), Perceive(y=76,h=48), Reason(y=159,h=48), Act(y=242,h=48), Observe(y=325,h=48)
  // Finish node at right: x=272,y=164,w=85,h=38
  // Loop-back path right side at x=270

  return (
    <div style={{
      border: '1px solid var(--border-color, #ccc)',
      borderRadius: 12, padding: '16px 12px', margin: '1.5rem 0',
      background: 'var(--panel-background, #fff)', userSelect: 'none',
    }}>
      <div style={{ overflowX: 'auto' }}>
        <svg viewBox="0 0 370 415" style={{ width: '100%', maxWidth: 370, display: 'block', margin: '0 auto' }}>
          <defs>
            {[
              ['off',    '#cccccc'],
              ['blue',   ACC.blue.stroke],
              ['green',  ACC.green.stroke],
              ['purple', ACC.purple.stroke],
              ['orange', ACC.orange.stroke],
            ].map(([key, color]) => (
              <marker key={key} id={`m-${key}`} markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
                <path d="M0,0 L0,7 L7,3.5 z" fill={color} />
              </marker>
            ))}
          </defs>

          {/* ── Arrows ── */}
          <path d="M 140,42 L 140,76" fill="none"
            stroke={as_('a-sp')} strokeWidth={on('a-sp') ? 2.5 : 1.5}
            markerEnd={am('a-sp')} style={{ transition: 'stroke 0.35s' }} />
          <text x={162} y={61} fontSize="10" fill={as_('a-sp')} style={{ transition: 'fill 0.35s' }}>new task</text>

          <path d="M 140,124 L 140,159" fill="none"
            stroke={as_('a-pr')} strokeWidth={on('a-pr') ? 2.5 : 1.5}
            markerEnd={am('a-pr')} style={{ transition: 'stroke 0.35s' }} />

          <path d="M 140,207 L 140,242" fill="none"
            stroke={as_('a-ra')} strokeWidth={on('a-ra') ? 2.5 : 1.5}
            markerEnd={am('a-ra')} style={{ transition: 'stroke 0.35s' }} />

          <path d="M 140,290 L 140,325" fill="none"
            stroke={as_('a-ao')} strokeWidth={on('a-ao') ? 2.5 : 1.5}
            markerEnd={am('a-ao')} style={{ transition: 'stroke 0.35s' }} />

          {/* Loop-back dashed arc (right side) */}
          <path d="M 233,349 L 270,349 L 270,100 L 233,100" fill="none"
            stroke={as_('a-op')} strokeWidth={on('a-op') ? 2.5 : 1.5}
            strokeDasharray="6 3"
            markerEnd={am('a-op')} style={{ transition: 'stroke 0.35s' }} />
          <text x={274} y={228} fontSize="10" textAnchor="start"
            fill={as_('a-op')} style={{ transition: 'fill 0.35s' }}>↺ loops back</text>

          {/* Reason → Finish */}
          <path d="M 233,183 L 272,183" fill="none"
            stroke={as_('a-rf')} strokeWidth={on('a-rf') ? 2.5 : 1.5}
            markerEnd={am('a-rf')} style={{ transition: 'stroke 0.35s' }} />
          {/* Label lifted into the open space above the box/arrow so it is not
              cramped against the Reason node, the arrow, or the loop-back line
              (x=270). Anchored to clear the box right edge (x=233). */}
          <text x={248} y={153} fontSize="10" textAnchor="middle"
            fill={as_('a-rf')} style={{ transition: 'fill 0.35s' }}>goal met</text>

          {/* ── Nodes ── */}
          {/* Start */}
          <rect x={80} y={12} width={120} height={30} rx={15}
            fill={nfill('n-start')} stroke={ns('n-start')} strokeWidth={on('n-start') ? 2 : 1.5}
            style={{ transition: 'all 0.35s', filter: on('n-start') ? `drop-shadow(0 0 5px ${ns('n-start')}88)` : 'none' }} />
          <text x={140} y={28} textAnchor="middle" dominantBaseline="middle"
            fontSize="13" fontWeight="500" fill={nt('n-start')} style={{ transition: 'fill 0.35s' }}>🏁 Start</text>

          {/* Perceive */}
          <rect x={47} y={76} width={186} height={48} rx={8}
            fill={nfill('n-perceive')} stroke={ns('n-perceive')} strokeWidth={on('n-perceive') ? 2 : 1.5}
            style={{ transition: 'all 0.35s', filter: on('n-perceive') ? `drop-shadow(0 0 5px ${ns('n-perceive')}88)` : 'none' }} />
          <text x={140} y={95} textAnchor="middle" dominantBaseline="middle"
            fontSize="13" fontWeight="500" fill={nt('n-perceive')} style={{ transition: 'fill 0.35s' }}>👀 Perceive</text>
          <text x={140} y={113} textAnchor="middle" dominantBaseline="middle"
            fontSize="10" fill="var(--text-secondary, #999)">current state</text>

          {/* Reason */}
          <rect x={47} y={159} width={186} height={48} rx={8}
            fill={nfill('n-reason')} stroke={ns('n-reason')} strokeWidth={on('n-reason') ? 2 : 1.5}
            style={{ transition: 'all 0.35s', filter: on('n-reason') ? `drop-shadow(0 0 5px ${ns('n-reason')}88)` : 'none' }} />
          <text x={140} y={178} textAnchor="middle" dominantBaseline="middle"
            fontSize="13" fontWeight="500" fill={nt('n-reason')} style={{ transition: 'fill 0.35s' }}>🧠 Reason</text>
          <text x={140} y={196} textAnchor="middle" dominantBaseline="middle"
            fontSize="10" fill="var(--text-secondary, #999)">LLM plans next step</text>

          {/* Act */}
          <rect x={47} y={242} width={186} height={48} rx={8}
            fill={nfill('n-act')} stroke={ns('n-act')} strokeWidth={on('n-act') ? 2 : 1.5}
            style={{ transition: 'all 0.35s', filter: on('n-act') ? `drop-shadow(0 0 5px ${ns('n-act')}88)` : 'none' }} />
          <text x={140} y={261} textAnchor="middle" dominantBaseline="middle"
            fontSize="13" fontWeight="500" fill={nt('n-act')} style={{ transition: 'fill 0.35s' }}>🛠️ Act</text>
          <text x={140} y={279} textAnchor="middle" dominantBaseline="middle"
            fontSize="10" fill="var(--text-secondary, #999)">use tools / call APIs</text>

          {/* Observe */}
          <rect x={47} y={325} width={186} height={48} rx={8}
            fill={nfill('n-observe')} stroke={ns('n-observe')} strokeWidth={on('n-observe') ? 2 : 1.5}
            style={{ transition: 'all 0.35s', filter: on('n-observe') ? `drop-shadow(0 0 5px ${ns('n-observe')}88)` : 'none' }} />
          <text x={140} y={344} textAnchor="middle" dominantBaseline="middle"
            fontSize="13" fontWeight="500" fill={nt('n-observe')} style={{ transition: 'fill 0.35s' }}>🧐 Observe</text>
          <text x={140} y={362} textAnchor="middle" dominantBaseline="middle"
            fontSize="10" fill="var(--text-secondary, #999)">read result</text>

          {/* Finish */}
          <rect x={272} y={164} width={85} height={38} rx={8}
            fill={nfill('n-finish')} stroke={ns('n-finish')} strokeWidth={on('n-finish') ? 2 : 1.5}
            style={{ transition: 'all 0.35s', filter: on('n-finish') ? `drop-shadow(0 0 5px ${ns('n-finish')}88)` : 'none' }} />
          <text x={314} y={183} textAnchor="middle" dominantBaseline="middle"
            fontSize="13" fontWeight="500" fill={nt('n-finish')} style={{ transition: 'fill 0.35s' }}>✅ Done</text>
        </svg>
      </div>

      {/* Step description */}
      <div style={{
        minHeight: 50, margin: '8px 0 6px', padding: '8px 12px',
        borderRadius: 8, background: 'var(--bg-secondary, #f5f5f5)',
        fontSize: '0.82rem', color: 'var(--text-secondary, #555)',
        textAlign: 'center', opacity: currentStepData ? 1 : 0.4,
      }}>
        {currentStepData
          ? <><strong style={{ color: 'var(--text-color, #333)' }}>Step {step}: {currentStepData.label}</strong> — {currentStepData.description}</>
          : 'Press play to walk through the agent reasoning loop.'}
      </div>

      {/* Controls — two fixed rows matching the other tutorials */}
      <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>

        {/* Row 1: progress dots (left) + status | Reset (right, always present) */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {STEPS.map((s, i) => (
              <button
                key={s.id}
                onClick={() => { setIsPlaying(false); setStep(i + 1); }}
                title={s.label}
                style={{
                  width: 10, height: 10, borderRadius: '50%', border: 'none',
                  padding: 0, cursor: 'pointer',
                  background: step === i + 1 ? '#0288d1' : step > i + 1 ? '#0288d155' : 'var(--border-color, #ccc)',
                  transition: 'background 0.3s',
                }}
              />
            ))}
            <span style={{ marginLeft: 8, fontSize: '0.78rem', color: 'var(--text-secondary, #888)', whiteSpace: 'nowrap' }}>
              {step === 0 ? 'Ready' : isPlaying ? 'Running…' : step >= maxStep ? 'Complete' : `Step ${step} / ${maxStep} — paused`}
            </span>
          </div>
          <button
            onClick={() => { setIsPlaying(false); setStep(0); }}
            disabled={step === 0}
            title="Reset"
            style={{
              display: 'flex', alignItems: 'center', gap: 4,
              padding: '0.28rem 0.6rem', fontSize: '0.78rem', fontWeight: 500,
              border: '1px solid var(--border-color, #ccc)',
              borderRadius: 6,
              background: 'transparent',
              color: 'var(--text-secondary, #666)',
              cursor: step === 0 ? 'default' : 'pointer',
              opacity: step === 0 ? 0.4 : 1,
            }}
          >
            <RiRefreshLine size={12} /> Reset
          </button>
        </div>

        {/* Row 2: ◀ · Play/Pause · ▶ — always three slots, no position shift */}
        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', justifyContent: 'center' }}>
          {/* ◀ Back — slot 1, always present */}
          <button
            onClick={() => { setIsPlaying(false); setStep(s => Math.max(0, s - 1)); }}
            disabled={step === 0}
            title="Previous step"
            style={{
              display: 'flex', alignItems: 'center',
              padding: '0.32rem 0.6rem', fontSize: '0.82rem',
              border: '1px solid var(--border-color, #ccc)',
              borderRadius: 6, background: 'transparent',
              color: step === 0 ? 'var(--text-secondary, #ccc)' : 'var(--text-color, #333)',
              cursor: step === 0 ? 'not-allowed' : 'pointer',
              opacity: step === 0 ? 0.4 : 1,
            }}
          >
            <RiSkipBackLine size={14} />
          </button>

          {/* Centre — slot 2, label/style changes only, position locked */}
          <button
            onClick={handlePlayPause}
            title={step >= maxStep ? 'Replay' : isPlaying ? 'Pause' : step === 0 ? 'Run' : 'Resume'}
            style={{
              display: 'flex', alignItems: 'center', gap: 4,
              padding: '0.32rem 0.85rem', fontSize: '0.82rem', fontWeight: 700,
              border: '1px solid #0288d1',
              borderRadius: 6,
              background: isPlaying ? 'transparent' : '#0288d1',
              color: isPlaying ? '#0288d1' : '#fff',
              cursor: 'pointer',
              minWidth: '5rem',
              transition: 'all 0.15s',
            }}
          >
            {isPlaying ? <RiPauseLine size={14} /> : <RiPlayCircleLine size={14} />}
            {step >= maxStep ? 'Replay' : isPlaying ? 'Pause' : step === 0 ? 'Run' : 'Resume'}
          </button>

          {/* ▶ Next — slot 3, always present */}
          <button
            onClick={() => { setIsPlaying(false); setStep(s => Math.min(maxStep, s + 1)); }}
            disabled={step >= maxStep}
            title="Next step"
            style={{
              display: 'flex', alignItems: 'center',
              padding: '0.32rem 0.6rem', fontSize: '0.82rem',
              border: '1px solid var(--border-color, #ccc)',
              borderRadius: 6, background: 'transparent',
              color: step >= maxStep ? 'var(--text-secondary, #ccc)' : 'var(--text-color, #333)',
              cursor: step >= maxStep ? 'not-allowed' : 'pointer',
              opacity: step >= maxStep ? 0.4 : 1,
            }}
          >
            <RiSkipForwardLine size={14} />
          </button>
        </div>

      </div>
    </div>
  );
}
